import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from "react-icons/fa";
import {
  Form,
  FormGroup,
  Label,
  Input,
  SubmitButton,
  ErrorMessage,
  SuccessMessage,
  PasswordWrapper,
  EyeIcon,
  ValidationList,
  ValidationItem,
  BackToLogin,
  InstructionText,
} from "./Login.styles.js";

const passwordRequirements = [
  { id: "length", text: "At least 8 characters", regex: /.{8,}/ },
  { id: "uppercase", text: "At least one uppercase letter", regex: /[A-Z]/ },
  { id: "lowercase", text: "At least one lowercase letter", regex: /[a-z]/ },
  { id: "number", text: "At least one number", regex: /\d/ },
  {
    id: "special",
    text: "At least one special character",
    regex: /[!@#$%^&*(),.?":{}|<>]/,
  },
];

const ResetPasswordForm = ({ token }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirm: false,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  const getPasswordStrength = (password) => {
    return passwordRequirements.map((req) => ({
      ...req,
      isValid: req.regex.test(password),
    }));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validation = getPasswordStrength(formData.newPassword);
    const allValid = validation.every((req) => req.isValid);

    if (!allValid) {
      setError("Please meet all password requirements");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || ""}/auth/update-password`,
        {
          token,
          newPassword: formData.newPassword,
        }
      );

      setSuccess(response.data.message);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/");
      }, 2500);
    } catch (err) {
      if (err.response?.status === 400) {
        setError("Invalid or expired reset token");
        setTokenValid(false);
      } else {
        setError(err.response?.data?.message || "Failed to reset password");
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = getPasswordStrength(formData.newPassword);
  const passwordsMatch = formData.newPassword === formData.confirmPassword;

  if (!tokenValid) {
    return (
      <>
        <ErrorMessage>Invalid or expired reset token</ErrorMessage>
        <BackToLogin onClick={() => navigate("/#forgot-password")}>
          ← Request a new reset link
        </BackToLogin>
      </>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <InstructionText>Create a new password for your account.</InstructionText>

      <FormGroup>
        <Label htmlFor="newPassword">New Password</Label>
        <PasswordWrapper>
          <Input
            type={showPassword.password ? "text" : "password"}
            id="newPassword"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            required
            placeholder="Create a new password"
            style={{ paddingRight: "40px" }}
          />
          <EyeIcon
            onClick={() =>
              setShowPassword((prev) => ({
                ...prev,
                password: !prev.password,
              }))
            }
          >
            {showPassword.password ? <FaEyeSlash /> : <FaEye />}
          </EyeIcon>
        </PasswordWrapper>

        {formData.newPassword && (
          <ValidationList>
            {passwordValidation.map((req) => (
              <ValidationItem key={req.id} isValid={req.isValid}>
                {req.isValid ? <FaCheck size={12} /> : <FaTimes size={12} />}
                {req.text}
              </ValidationItem>
            ))}
          </ValidationList>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="confirmPassword">Confirm New Password</Label>
        <PasswordWrapper>
          <Input
            type={showPassword.confirm ? "text" : "password"}
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            placeholder="Confirm your new password"
            style={{ paddingRight: "40px" }}
          />
          <EyeIcon
            onClick={() =>
              setShowPassword((prev) => ({
                ...prev,
                confirm: !prev.confirm,
              }))
            }
          >
            {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
          </EyeIcon>
        </PasswordWrapper>
        {formData.confirmPassword && (
          <ValidationItem isValid={passwordsMatch}>
            Passwords match
          </ValidationItem>
        )}
      </FormGroup>

      <SubmitButton type="submit" disabled={loading}>
        {loading ? "Resetting..." : "Reset Password"}
      </SubmitButton>

      <BackToLogin onClick={() => navigate("/")}>← Back to Login</BackToLogin>
    </Form>
  );
};

export default ResetPasswordForm;
